import { useEffect, useMemo, useReducer, useState, type Reducer } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  useOrderPageSortingAtom,
  useOrderTableLoading,
} from "@/atoms/order-page-atoms";
import { type QueryLazyOptions } from "@apollo/client";
import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import {
  DataTypeProvider,
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  SortingState,
  VirtualTableState,
  createRowCache,
  type Sorting,
  type SortingDirection,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  TableColumnResizing,
  TableFilterRow,
  TableHeaderRow,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import {
  type FetchOrderSummary,
  type FetchOrderSummaryVariables,
  type SubscriptionTypeEnum,
} from "@north-beam/nb-client-side";
import { useOrdersPageFilters } from "@pages/orders/use-orders-filters";
import {
  TableHeaderRowCell,
  TableHeaderRowContent,
  TableHeaderRowSortLabel,
} from "@pages/sales/tab-table-section/table/table-components";
import { FETCH_ORDER_SUMMARY } from "@pages/settings/queries";

import { useIsAllowedToSeeCustomerDetails } from "../use-is-allowed-to-see-customer-details";

import { VIRTUAL_PAGE_SIZE } from "./constant";
import {
  AttributedEditor,
  CustomerIdEditor,
  CustomerTagsEditor,
  DiscountCodesEditor,
  DiscountValueEditor,
  OrderIdEditor,
  OrderNumberEditor,
  OrderNumberOfOriginalItemsEditor,
  OrderTagsEditor,
  OrderTouchpointsEditor,
  OrderTypesEditor,
  OrderValueEditor,
  ProductEditor,
  RefundValueEditor,
  ShippingValueEditor,
  SourceNameEditor,
  SubscriptionsEditor,
  TaxValueEditor,
} from "./editors";
import {
  AttributedDataFormatter,
  CurrencyDataFormatter,
  CustomerTagsDataFormatter,
  CustomerTypeDataFormatter,
  DateTypeProvider,
  DiscountCodesDataFormatter,
  OrderIdDataFormatter,
  OrderNumberDataFormatter,
  OrderTouchpointsFormatter,
  ProductsPurchasedDataFormatter,
  SourceNameDataFormatter,
  SubscriptionsDataFormatter,
  TagsDataFormatter,
  ValueDataFormatter,
  dateComparator,
} from "./formatters";
import {
  initialState,
  reducer,
  type OrderAction,
  type OrderTableState,
} from "./reducer";

export type OrderTableProps = {
  totalCount: number;
  isLoading: boolean;
  loadOrders: (
    options?: QueryLazyOptions<FetchOrderSummaryVariables> | undefined,
  ) => void;
  limit: number;
  orders: {
    orderId: string;
    orderNumber: string;
    date: Date;
    orderValue: number;
    discountValue: number;
    shippingValue: number;
    taxValue: number;
    refundValue: number;
    numberOfOriginalItems: number;
    customerId: string;
    customerType: string;
    numberOfTouchpoints: number;
    isAttributed: string;
    tags: string[];
    sourceName: string[];
    customerTags: string[];
    discountCodes: string[];
    productsPurchased: string[];
    subscriptionType: SubscriptionTypeEnum[];
  }[];
};

export function OrdersTable() {
  const { user } = useUser();
  const [, setOrderQueryLoading] = useOrderTableLoading();
  const { addQueryMetadata } = useQueryMetadata();

  // filter options
  const filterOptions = useOrdersPageFilters();

  const [state, dispatch] = useReducer<Reducer<OrderTableState, OrderAction>>(
    reducer,
    initialState,
  );
  const [sorting, setSorting] = useOrderPageSortingAtom();

  const handleSorting = (sorting: Sorting[]) => {
    const newSorting = sorting?.[0]
      ? {
          sortingField: sorting?.[0].columnName,
          sortingOrder: sorting?.[0].direction,
        }
      : null;
    setSorting(newSorting);
  };

  const cache = useMemo(() => createRowCache(VIRTUAL_PAGE_SIZE), []);

  useEffect(() => {
    cache.invalidate();
    dispatch({ type: "RESET" });
  }, [cache, filterOptions, sorting]);

  const updateRows = (skip: number, count: number, newTotalCount?: number) => {
    const maxRows = 1_000_000;

    dispatch({
      type: "UPDATE_ROWS",
      payload: {
        skip,
        rows: cache.getRows(skip, count),
        totalCount:
          newTotalCount && newTotalCount < maxRows ? newTotalCount : maxRows,
      },
    });
  };

  const getRemoteRows = (requestedSkip: number, take: number) => {
    dispatch({ type: "START_LOADING", payload: { requestedSkip, take } });
  };

  const isAllowedToSeeCustomerDetails = useIsAllowedToSeeCustomerDetails();

  const [columns] = useState([
    { name: "orderId", title: "Order ID" },
    { name: "orderNumber", title: "Order Number" },
    { name: "date", title: "Date" },
    { name: "orderValue", title: "Order Value" },
    { name: "discountValue", title: "Discount Value" },
    { name: "shippingValue", title: "Shipping Value" },
    { name: "taxValue", title: "Tax Value" },
    { name: "refundValue", title: "Refund Value" },
    { name: "numberOfOriginalItems", title: "No. of original Items" },
    ...(isAllowedToSeeCustomerDetails
      ? [{ name: "customerId", title: "Customer Id" }]
      : []),
    { name: "customerType", title: "Customer Type" },
    { name: "numberOfTouchpoints", title: "No. of Touchpoints" },
    { name: "isAttributed", title: "Attributed?" },
    { name: "tags", title: "Tags" },
    { name: "sourceName", title: "Source Name" },
    { name: "customerTags", title: "Customer Tags" },
    ...(user.featureFlags.showSubscriptionOrderTags
      ? [
          {
            name: "subscriptionType",
            title: "Subscription",
          },
        ]
      : []),
    { name: "productsPurchased", title: "Products Purchased" },
    { name: "discountCodes", title: "Discount Codes" },
  ]);

  const loadData = () => {
    const { requestedSkip, take, lastQuery, loading } = state;
    const query = `filterOptions=${JSON.stringify(
      filterOptions,
    )}&requestedSkip=${requestedSkip}&take=${take}`;

    if (query !== lastQuery && !loading) {
      const cached = cache.getRows(requestedSkip, take);
      if (cached.length === take) {
        updateRows(requestedSkip, take);
      } else {
        setOrderQueryLoading(true);
        dispatch({ type: "FETCH_INIT" });

        user.apolloClient
          .query<FetchOrderSummary, FetchOrderSummaryVariables>({
            query: FETCH_ORDER_SUMMARY,
            variables: {
              ...(sorting
                ? {
                    sorting: {
                      sortingField: sorting.sortingField as any,
                      sortingOrder: sorting.sortingOrder as any,
                    },
                  }
                : {}),
              filterOptions,
              limit: take,
              offset: requestedSkip as number,
            },
          })
          .then(
            ({
              data: {
                me: {
                  orders: {
                    data,
                    totalCount: newTotalCount,
                    queryMetadata: loadedQueryMetadata,
                  },
                },
              },
            }) => {
              addQueryMetadata({
                context: "Orders",
                topic: "Orders Table",
                metadata: loadedQueryMetadata,
              });

              const orders = (data || []).map((order) => {
                const numberOfOriginalItems = order.products.reduce(
                  (acc, curr) => acc + curr.quantity,
                  0,
                );

                return {
                  conversionEventId: order.conversionEventId,
                  orderId: order.orderId,
                  orderNumber: order.orderNumber,
                  date: new Date(order.occurredAt.value),
                  orderValue: order.revenueInDollars,
                  discountValue: order.discountValue,
                  shippingValue: order.shippingValue,
                  taxValue: order.taxValue,
                  refundValue: order.refundAmountInDollars,
                  numberOfOriginalItems,
                  customerId: order.customerId,
                  customerType: order.orderType,
                  numberOfTouchpoints: order.numberOfTouchpoints,
                  isAttributed: order.attributed ? "Yes" : "No",
                  tags: order.orderTags,
                  sourceName: order.sourceName,
                  customerTags: order.customerTags,
                  subscriptionType: order.subscriptionType,
                  productsPurchased: order.products.map((p) => p.title),
                  discountCodes: order.discountCodes,
                };
              });

              cache.setRows(requestedSkip, orders);
              updateRows(requestedSkip, take, newTotalCount);
              setOrderQueryLoading(false);
            },
          )
          .catch(() => dispatch({ type: "REQUEST_ERROR" }));
      }
      dispatch({ type: "UPDATE_QUERY", payload: { lastQuery: query } });
    }
  };

  useEffect(() => loadData());

  const { rows, skip, totalCount, loading } = state;

  return (
    <>
      <div className="my-4 bg-white">
        <Grid
          rows={rows}
          columns={columns}
          getRowId={(row) => `${row.orderId}-${uuidv4()}`}
        >
          <FilteringState
            defaultFilters={[]}
            columnExtensions={[{ columnName: "date", filteringEnabled: false }]}
          />
          <IntegratedFiltering />
          <SortingState
            columnExtensions={[
              { columnName: "orderId", sortingEnabled: false },
              { columnName: "orderNumber", sortingEnabled: false },
              { columnName: "customerId", sortingEnabled: false },
              { columnName: "customerType", sortingEnabled: false },
              { columnName: "isAttributed", sortingEnabled: false },
              { columnName: "tags", sortingEnabled: false },
              { columnName: "sourceName", sortingEnabled: false },
              { columnName: "subscriptionType", sortingEnabled: false },
              { columnName: "productsPurchased", sortingEnabled: false },
              { columnName: "customerTags", sortingEnabled: false },
              { columnName: "discountCodes", sortingEnabled: false },
            ]}
            sorting={
              sorting
                ? [
                    {
                      columnName: sorting?.sortingField,
                      direction: sorting?.sortingOrder as SortingDirection,
                    },
                  ]
                : []
            }
            onSortingChange={handleSorting}
          />
          <IntegratedSorting
            columnExtensions={[
              {
                columnName: "date",
                compare: dateComparator,
              },
            ]}
          />
          {/* Custom Cells */}
          <DateTypeProvider for={["date"]} />
          <DataTypeProvider
            for={["subscriptionType"]}
            formatterComponent={SubscriptionsDataFormatter}
            editorComponent={SubscriptionsEditor}
          />
          <DataTypeProvider
            for={["tags"]}
            formatterComponent={TagsDataFormatter}
            editorComponent={OrderTagsEditor}
          />
          <DataTypeProvider
            for={["sourceName"]}
            formatterComponent={SourceNameDataFormatter}
            editorComponent={SourceNameEditor}
          />
          <DataTypeProvider
            for={["customerTags"]}
            formatterComponent={CustomerTagsDataFormatter}
            editorComponent={CustomerTagsEditor}
          />
          <DataTypeProvider
            for={["productsPurchased"]}
            formatterComponent={ProductsPurchasedDataFormatter}
            editorComponent={ProductEditor}
          />
          <DataTypeProvider
            for={["discountCodes"]}
            formatterComponent={DiscountCodesDataFormatter}
            editorComponent={DiscountCodesEditor}
          />
          <DataTypeProvider
            for={["orderValue"]}
            formatterComponent={CurrencyDataFormatter}
            editorComponent={OrderValueEditor}
          />
          <DataTypeProvider
            for={["discountValue"]}
            formatterComponent={CurrencyDataFormatter}
            editorComponent={DiscountValueEditor}
          />
          <DataTypeProvider
            for={["shippingValue"]}
            formatterComponent={CurrencyDataFormatter}
            editorComponent={ShippingValueEditor}
          />
          <DataTypeProvider
            for={["taxValue"]}
            formatterComponent={CurrencyDataFormatter}
            editorComponent={TaxValueEditor}
          />
          <DataTypeProvider
            for={["refundValue"]}
            formatterComponent={CurrencyDataFormatter}
            editorComponent={RefundValueEditor}
          />
          <DataTypeProvider
            for={["isAttributed"]}
            formatterComponent={AttributedDataFormatter}
            editorComponent={AttributedEditor}
          />
          <DataTypeProvider
            for={["customerId"]}
            formatterComponent={ValueDataFormatter}
            editorComponent={CustomerIdEditor}
          />
          <DataTypeProvider
            for={["customerType"]}
            formatterComponent={CustomerTypeDataFormatter}
            editorComponent={OrderTypesEditor}
          />
          <DataTypeProvider
            for={["orderId"]}
            formatterComponent={OrderIdDataFormatter}
            editorComponent={OrderIdEditor}
          />
          <DataTypeProvider
            for={["orderNumber"]}
            formatterComponent={OrderNumberDataFormatter}
            editorComponent={OrderNumberEditor}
          />
          <DataTypeProvider
            for={["numberOfTouchpoints"]}
            formatterComponent={OrderTouchpointsFormatter}
            editorComponent={OrderTouchpointsEditor}
          />
          <DataTypeProvider
            for={["numberOfOriginalItems"]}
            formatterComponent={ValueDataFormatter}
            editorComponent={OrderNumberOfOriginalItemsEditor}
          />
          <VirtualTableState
            infiniteScrolling
            loading={loading}
            totalRowCount={totalCount}
            pageSize={VIRTUAL_PAGE_SIZE}
            skip={skip}
            getRows={getRemoteRows}
          />
          <VirtualTable
            height="43rem"
            estimatedRowHeight={46}
            columnExtensions={[
              { columnName: "orderId", align: "right" },
              { columnName: "orderNumber", align: "right" },
              { columnName: "orderValue", align: "right" },
              { columnName: "discountValue", align: "right" },
              { columnName: "shippingValue", align: "right" },
              { columnName: "taxValue", align: "right" },
              { columnName: "refundValue", align: "right" },
              { columnName: "numberOfOriginalItems", align: "right" },
              { columnName: "numberOfTouchpoints", align: "right" },
            ]}
          />
          <TableColumnResizing
            defaultColumnWidths={[
              { columnName: "orderId", width: 100 },
              { columnName: "orderNumber", width: 100 },
              { columnName: "date", width: 100 },
              { columnName: "orderValue", width: 175 },
              { columnName: "discountValue", width: 175 },
              { columnName: "shippingValue", width: 175 },
              { columnName: "taxValue", width: 175 },
              { columnName: "refundValue", width: 175 },
              { columnName: "numberOfOriginalItems", width: 175 },
              { columnName: "customerId", width: 100 },
              { columnName: "customerType", width: 150 },
              { columnName: "numberOfTouchpoints", width: 175 },
              { columnName: "isAttributed", width: 100 },
              { columnName: "tags", width: 120 },
              { columnName: "sourceName", width: 120 },
              { columnName: "customerTags", width: 120 },
              { columnName: "subscriptionType", width: 120 },
              { columnName: "productsPurchased", width: 250 },
              { columnName: "discountCodes", width: 250 },
            ]}
          />
          <TableHeaderRow
            showSortingControls
            contentComponent={TableHeaderRowContent}
            sortLabelComponent={TableHeaderRowSortLabel}
            cellComponent={TableHeaderRowCell}
          />
          <TableFilterRow />
        </Grid>
      </div>
    </>
  );
}
